// ─────────────────────────────────────────────────────────────────────────────
// src/pages/Home.tsx
//
// Shell layout:
//   [AppBar]  sticky top:0 h:60
//   [<main>]  scrollable content
//   [Footer]
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef } from "react";
import { useBreakpoint } from "../shared/ui/useBreakpoint";
import QueryBuilder from "../features/cube/components/QueryBuilder";
import VentesView from "../features/dimension-analysis/views/VentesView";
import AppBar, { Page } from "../layout/AppBar";
import Footer from "../layout/Footer";
import SaisonView from "../features/dimension-analysis/views/SaisonView";
import GroupeView from "../features/dimension-analysis/views/GroupeView";
import UniversView from "../features/dimension-analysis/views/UniversView";
import SousFamilleView from "../features/dimension-analysis/views/SousFamilleView";
import MagasinView from "../features/dimension-analysis/views/MagasinView";
import MarqueView from "../features/dimension-analysis/views/MarqueView";
import TailleView from "../features/dimension-analysis/views/TailleView";
import CouleurView from "../features/dimension-analysis/views/CouleurView";
import FamilleView from "../features/dimension-analysis/views/FamilleView";
import VendeurView from "../features/dimension-analysis/views/VendeurView";
import Dashboard from "../features/cube/components/Dashboard";
import DashboardPage from "./DashboardPage";
import CouleurTailleView from "../features/dimension-analysis/views/CouleurTailleView";
import ProfilePage from "./ProfilePage";
import PrevisionFView from "../features/forecast/components/PrevisionFView";
import PrevisionFContainer from "../features/forecast/components/PrevisionFContainer";
import AttributionRoles from "../features/users/components/AttributionRoles";
import { colors } from "../shared/ui/tokens";

// ── Palette claire AVDASH ───────────────────────────────────────────────────
const NW = {
  bgPage: colors.neutral[0],
  bgSection: colors.neutral[100],
  bgCard: colors.neutral[0],
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.accent[300],
  violetLight: colors.brand[100],
  violetSoft: colors.neutral[100],
  violetBorder: "rgba(29,91,168,0.18)",
  mauve: colors.accent[300],
  rose: colors.brand[400],
  rosePale: colors.neutral[100],
  textTitle: colors.neutral[900],
  textBody: colors.brand[900],
  textMuted: colors.brand[500],
  green: colors.accent[400],
  white: colors.neutral[0],
  border: "rgba(29,91,168,0.14)",
  shadow: "0 4px 24px rgba(29,91,168,0.10)",
  shadowHover: "0 16px 48px rgba(29,91,168,0.22)",
};

// ── Module cards ────────────────────────────────────────────────────────────
const STATS = [
  {
    icon: "⬡",
    label: "MDX Query Builder",
    desc: "Construisez et exécutez des requêtes MDX avancées sur votre cube SSAS",
    color: "#1D5BA8",
    colorBg: "#F1F5F9",
    colorBorder: "rgba(29,91,168,0.22)",
    page: "mdx" as Page,
  },
  {
    icon: "◇",
    label: "Magazins",
    desc: "Comparez le chiffre d'affaires et les volumes écoulés point de vente par point de vente",
    color: "#00806F",
    colorBg: "#F8FAFC",
    colorBorder: "rgba(127,200,189,0.22)",
    page: "magazin" as Page,
  },
  {
    icon: "◈",
    label: "Groupes marques",
    desc: "Évaluez la contribution de chaque groupe de marques au CA global et suivez leur évolution",
    color: "#2077BC",
    colorBg: "#F1F5F9",
    colorBorder: "rgba(74,134,199,0.22)",
    page: "marques" as Page,
  },
  {
    icon: "◈",
    label: "Groupes Article",
    desc: "Identifiez les groupes d'articles les plus performants et détectez les opportunités de croissance",
    color: "#1D5BA8",
    colorBg: "#F1F5F9",
    colorBorder: "rgba(29,91,168,0.22)",
    page: "ventes" as Page,
  },
  {
    icon: "◇",
    label: "Sous Familles",
    desc: "Explorez les données détaillées par sous-famille de produits",
    color: "#00806F",
    colorBg: "#F8FAFC",
    colorBorder: "rgba(127,200,189,0.22)",
    page: "sousfamille" as Page,
  },
  {
    icon: "⟐",
    label: "Analyse Saison",
    desc: "Identifiez les tendances et variations saisonnières de votre activité",
    color: "#2077BC",
    colorBg: "#F1F5F9",
    colorBorder: "rgba(74,134,199,0.22)",
    page: "saison" as Page,
  },
  {
    icon: "⊞",
    label: "Analyse Groupe",
    desc: "Comparez le chiffre d'affaires et les quantités vendues par groupe",
    color: "#1D5BA8",
    colorBg: "#F1F5F9",
    colorBorder: "rgba(29,91,168,0.22)",
    page: "groupe" as Page,
  },
  {
    icon: "◍",
    label: "Analyse Univers",
    desc: "Suivez la performance de chaque univers produit et son évolution dans le temps",
    color: "#00806F",
    colorBg: "#F8FAFC",
    colorBorder: "rgba(127,200,189,0.22)",
    page: "univers" as Page,
  },
  {
    icon: "◈",
    label: "Vendeurs",
    desc: "Classez et suivez les performances individuelles de chaque vendeur sur la période sélectionnée",
    color: "#1D5BA8",
    colorBg: "#F1F5F9",
    colorBorder: "rgba(29,91,168,0.22)",
    page: "vendeur" as Page,
  },
  {
    icon: "📏",
    label: "Analyse Taille",
    desc: "Visualisez la répartition des ventes par taille et optimisez votre assortiment dimensionnel",
    color: "#00806F",
    colorBg: "#F8FAFC",
    colorBorder: "rgba(127,200,189,0.22)",
    page: "taille" as Page,
  },
  {
    icon: "◇",
    label: "Analyse Couleur",
    desc: "Découvrez quelles couleurs génèrent le plus de ventes et affinez votre stratégie merchandising",
    color: "#2077BC",
    colorBg: "#F1F5F9",
    colorBorder: "rgba(74,134,199,0.22)",
    page: "couleur" as Page,
  },
  {
    icon: "◈",
    label: "Couleur Taille",
    desc: "Analyse croisée des ventes par couleur et taille pour identifier les meilleures combinaisons",
    color: "#1D5BA8",
    colorBg: "#F1F5F9",
    colorBorder: "rgba(29,91,168,0.22)",
    page: "couleur-taille" as Page,
  },
  {
    icon: "◇",
    label: "Familles",
    desc: "Explorez les données détaillées par famille de produits",
    color: "#00806F",
    colorBg: "#F8FAFC",
    colorBorder: "rgba(127,200,189,0.22)",
    page: "famille" as Page,
  },
  {
    icon: "⬡",
    label: "Analyse",
    desc: "Construisez et exécutez des requêtes MDX avancées sur votre cube SSAS",
    color: "#1D5BA8",
    colorBg: "#F1F5F9",
    colorBorder: "rgba(29,91,168,0.22)",
    page: "analyse" as Page,
  },
  {
    icon: "🔮",
    label: "Prévision Famille",
    desc: "Prévision du chiffre d'affaires par famille ou sous-famille avec découpage 70% train / 20% test / 10% validation",
    color: "#2077BC",
    colorBg: "#F1F5F9",
    colorBorder: "rgba(74,134,199,0.22)",
    page: "prevision_f" as Page,
  },
];

// ── Barre de stats rapides ───────────────────────────────────────────────────
const QUICK_STATS = [
  { value: "SSAS", label: "Connecté", color: "#1D5BA8" },
  { value: "11", label: "Modules actifs", color: "#1D5BA8" },
  { value: "Live", label: "Temps réel", color: "#1D5BA8" },
  { value: "BI", label: "Intelligence", color: "#1D5BA8" },
];

// ── AccueilPage ──────────────────────────────────────────────────────────────
export const AccueilPage: React.FC<{ onNavigate: (p: Page) => void }> = ({
  onNavigate,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useBreakpoint();

  const scrollBy = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    const cardWidth = 270; // approx card width + gap
    scrollRef.current.scrollBy({
      left: dir === "right" ? cardWidth : -cardWidth,
      behavior: "smooth",
    });
  };

  return (
    <div
      style={{
        width: "100%",
        minHeight: "calc(100vh - 102px)", // 62px AppBar + 40px TabBar
        background: NW.bgPage,
        position: "relative",
        overflow: "visible", // ← "hidden" cachait les cartes
      }}
    >
      {/* ── Décor de fond léger ── */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-8%",
            left: "-6%",
            width: 560,
            height: 560,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(29,91,168,0.06) 0%, transparent 70%)",
            animation: "blobDrift1 16s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "0%",
            right: "-6%",
            width: 480,
            height: 480,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(127,200,189,0.07) 0%, transparent 70%)",
            animation: "blobDrift2 20s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "35%",
            left: "38%",
            width: 340,
            height: 340,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(29,91,168,0.05) 0%, transparent 70%)",
            animation: "blobDrift3 24s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `radial-gradient(circle, rgba(29,91,168,0.09) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      {/* ── Hero ── */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: isMobile ? "40px 16px 32px" : "72px 24px 52px",
          textAlign: "center",
        }}
      >
        {/* Badge live */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 18px",
            background: NW.violetSoft,
            border: `1.5px solid ${NW.violetBorder}`,
            borderRadius: 99,
            fontSize: 11,
            fontWeight: 800,
            color: NW.violet,
            letterSpacing: 1.8,
            textTransform: "uppercase" as const,
            marginBottom: 28,
            boxShadow: "0 2px 12px rgba(29,91,168,0.08)",
            animation: "fadeSlideUp 0.5s 0.05s both",
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: NW.green,
              boxShadow: `0 0 8px ${NW.green}`,
              display: "inline-block",
              animation: "pulseGreen 2s infinite",
            }}
          />
          Plateforme BI — SSAS Live
        </div>

        {/* Titre principal */}
        <h1
          style={{
            fontFamily: "Poppins, Inter, system-ui, sans-serif",
            fontWeight: 800,
            fontSize: "clamp(2.2rem, 5.5vw, 4rem)",
            lineHeight: 1.08,
            margin: "0 0 12px",
            letterSpacing: "-2px",
            maxWidth: 720,
            animation: "fadeSlideUp 0.55s 0.12s both",
          }}
        >
          <span
            style={{
              color: NW.violetDark,
            }}
          >
            Avantages Soft
          </span>
          <br />
          <span
            style={{
              fontWeight: 500,
              fontSize: "0.52em",
              letterSpacing: "-0.2px",
              color: NW.textBody,
              WebkitTextFillColor: NW.textBody,
              background: "none",
            }}
          >
            Intelligence Platform
          </span>
        </h1>

        {/* Sous-titre */}
        <p
          style={{
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            fontSize: 16,
            color: NW.textBody,
            maxWidth: 540,
            lineHeight: 1.7,
            margin: isMobile ? "0 0 28px" : "0 0 48px",
            animation: "fadeSlideUp 0.55s 0.20s both",
            fontWeight: 500,
          }}
        >
          Explorez, analysez et visualisez vos données OLAP en temps réel.
          Requêtes MDX avancées, analyses multi-dimensionnelles et rapports
          dynamiques.
        </p>

        {/* Barre stats rapides */}
        <div
          style={{
            display: isMobile ? "grid" : "flex",
            gridTemplateColumns: "1fr 1fr",
            width: isMobile ? "100%" : undefined,
            gap: 0,
            background: NW.bgPage,
            border: `1.5px solid ${NW.border}`,
            borderRadius: 16,
            overflow: "hidden",
            marginBottom: isMobile ? 36 : 70,
            boxShadow: NW.shadow,
            animation: "fadeSlideUp 0.55s 0.28s both",
          }}
        >
          {QUICK_STATS.map((s, i) => (
            <div
              key={i}
              style={{
                padding: isMobile ? "14px 10px" : "18px 36px",
                borderRight:
                  (isMobile ? i % 2 === 0 : i < QUICK_STATS.length - 1)
                    ? `1px solid ${NW.border}`
                    : "none",
                borderBottom:
                  isMobile && i < QUICK_STATS.length - 2
                    ? `1px solid ${NW.border}`
                    : "none",
                textAlign: "center",
                background: i % 2 === 0 ? NW.bgPage : NW.bgSection,
              }}
            >
              <div
                style={{
                  fontFamily: "Poppins, Inter, system-ui, sans-serif",
                  fontWeight: 800,
                  fontSize: 22,
                  color: s.color,
                  lineHeight: 1,
                  letterSpacing: "-0.5px",
                }}
              >
                {s.value}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: NW.textMuted,
                  marginTop: 5,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  textTransform: "uppercase" as const,
                }}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Section modules ── */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          background: NW.bgSection,
          borderTop: `1px solid ${NW.border}`,
          padding: isMobile ? "32px 16px 48px" : "56px 24px 80px",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {/* Titre section */}
          <div
            style={{
              textAlign: "center",
              marginBottom: isMobile ? 24 : 44,
              animation: "fadeSlideUp 0.5s 0.36s both",
            }}
          >
            <div
              style={{
                fontFamily: "Poppins, Inter, system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 11,
                letterSpacing: 3.5,
                textTransform: "uppercase" as const,
                color: "#00806F", // colors.accent[600] — vert logo lisible
                marginBottom: 10,
              }}
            >
              Modules disponibles
            </div>
            <h2
              style={{
                fontFamily: "Poppins, Inter, system-ui, sans-serif",
                fontWeight: 800,
                fontSize: "clamp(1.5rem, 3vw, 2.2rem)",
                color: NW.textTitle,
                margin: 0,
                letterSpacing: "-0.8px",
              }}
            >
              Accédez à vos analyses
            </h2>
            <p
              style={{
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontSize: 14,
                color: NW.textMuted,
                marginTop: 10,
                fontWeight: 500,
              }}
            >
              Cliquez sur un module pour démarrer votre analyse
            </p>
          </div>

          {/* ── Cards carousel with navigation arrows ── */}
          <div style={{ position: "relative" }}>
            {/* Left arrow */}
            <button
              onClick={() => scrollBy("left")}
              title="Précédent"
              style={{
                position: "absolute",
                display: isMobile ? "none" : "flex",
                left: -22,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: `1.5px solid ${NW.violetBorder}`,
                background: NW.white,
                boxShadow: "0 4px 14px rgba(29,91,168,0.14)",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: NW.violet,
                fontSize: 16,
                transition: "all 0.18s",
                lineHeight: 1,
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.background = NW.violetSoft;
                el.style.boxShadow = `0 6px 20px rgba(29,91,168,0.22)`;
                el.style.borderColor = NW.violet;
                el.style.transform = "translateY(-50%) scale(1.1)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.background = NW.white;
                el.style.boxShadow = "0 4px 14px rgba(29,91,168,0.14)";
                el.style.borderColor = NW.violetBorder;
                el.style.transform = "translateY(-50%) scale(1)";
              }}
            >
              ‹
            </button>

            {/* Right arrow */}
            <button
              onClick={() => scrollBy("right")}
              title="Suivant"
              style={{
                position: "absolute",
                display: isMobile ? "none" : "flex",
                right: -22,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: `1.5px solid ${NW.violetBorder}`,
                background: NW.white,
                boxShadow: "0 4px 14px rgba(29,91,168,0.14)",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: NW.violet,
                fontSize: 16,
                transition: "all 0.18s",
                lineHeight: 1,
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.background = NW.violetSoft;
                el.style.boxShadow = `0 6px 20px rgba(29,91,168,0.22)`;
                el.style.borderColor = NW.violet;
                el.style.transform = "translateY(-50%) scale(1.1)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.background = NW.white;
                el.style.boxShadow = "0 4px 14px rgba(29,91,168,0.14)";
                el.style.borderColor = NW.violetBorder;
                el.style.transform = "translateY(-50%) scale(1)";
              }}
            >
              ›
            </button>

            {/* Scrollable cards container */}
            <div
              ref={scrollRef}
              data-cards-scroll
              style={{
                display: "flex",
                gap: 22,
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                paddingBottom: 6,
              }}
            >
              <style>{`
                div[data-cards-scroll]::-webkit-scrollbar { display: none; }
              `}</style>
              {STATS.map((s, i) => (
                <button
                  key={s.page}
                  onClick={() => onNavigate(s.page)}
                  style={{
                    background: s.colorBg,
                    border: `1.5px solid ${s.colorBorder}`,
                    borderRadius: 20,
                    padding: "28px 24px 24px",
                    cursor: "pointer",
                    textAlign: "left",
                    transition:
                      "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
                    boxShadow: "0 2px 12px rgba(29,91,168,0.07)",
                    animation: `fadeSlideUp 0.5s ${0.4 + i * 0.09}s both`,
                    position: "relative",
                    overflow: "hidden",
                    /* carousel sizing */
                    minWidth: 240,
                    flex: "0 0 240px",
                    scrollSnapAlign: "start",
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.transform = "translateY(-6px)";
                    el.style.boxShadow = `0 20px 48px rgba(29,91,168,0.18), 0 0 0 2px ${s.color}33`;
                    el.style.borderColor = `${s.color}55`;
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.transform = "translateY(0)";
                    el.style.boxShadow = "0 2px 12px rgba(29,91,168,0.07)";
                    el.style.borderColor = s.colorBorder;
                  }}
                >
                  {/* Glow décoratif coin */}
                  <div
                    style={{
                      position: "absolute",
                      top: -20,
                      right: -20,
                      width: 90,
                      height: 90,
                      borderRadius: "50%",
                      background: `radial-gradient(circle, ${s.color}18, transparent 70%)`,
                      pointerEvents: "none",
                    }}
                  />

                  {/* Icône */}
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 14,
                      background: NW.bgPage,
                      border: `1.5px solid ${s.colorBorder}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 24,
                      color: s.color,
                      marginBottom: 18,
                      boxShadow: `0 4px 14px ${s.color}18`,
                    }}
                  >
                    {s.icon}
                  </div>

                  {/* Titre carte */}
                  <div
                    style={{
                      fontFamily: "Poppins, Inter, system-ui, sans-serif",
                      fontWeight: 800,
                      fontSize: 15,
                      color: NW.textTitle,
                      marginBottom: 9,
                      letterSpacing: "-0.3px",
                    }}
                  >
                    {s.label}
                  </div>

                  {/* Description */}
                  <div
                    style={{
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                      fontSize: 13,
                      color: NW.textBody,
                      lineHeight: 1.55,
                      fontWeight: 500,
                    }}
                  >
                    {s.desc}
                  </div>

                  {/* Lien action */}
                  <div
                    style={{
                      marginTop: 20,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 12,
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                      fontWeight: 800,
                      color: s.color,
                      letterSpacing: 0.3,
                      background: `${s.color}14`,
                      padding: "5px 12px",
                      borderRadius: 99,
                      border: `1px solid ${s.color}28`,
                    }}
                  >
                    Ouvrir le module
                    <span style={{ fontSize: 13 }}>→</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer déco ── */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          background: NW.bgPage,
          borderTop: `1px solid ${NW.border}`,
          padding: "28px 24px",
          textAlign: "center",
        }}
      ></div>

      <style>{`
        @keyframes blobDrift1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(28px, -18px) scale(1.05); }
          66% { transform: translate(-12px, 22px) scale(0.97); }
        }
        @keyframes blobDrift2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          40% { transform: translate(-22px, 18px) scale(1.04); }
          70% { transform: translate(18px, -14px) scale(0.98); }
        }
        @keyframes blobDrift3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-18px, -22px) scale(1.07); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseGreen {
          0%, 100% { box-shadow: 0 0 7px #4DB3A5; }
          50% { box-shadow: 0 0 14px #4DB3A5, 0 0 4px #4DB3A5; }
        }
      `}</style>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Home
// ─────────────────────────────────────────────────────────────────────────────
const Home: React.FC = () => {
  const [page, setPage] = useState<Page>("accueil");

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--muted)",
        margin: 0,
        padding: 0,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* AppBar – sticky, z:100 */}
      <AppBar page={page} setPage={setPage} />

      {/* Main content */}
      <main
        style={{
          flex: 1,
          paddingTop: page === "accueil" || page === "profile" ? 0 : 20,
          paddingLeft: page === "accueil" || page === "profile" ? 0 : 24,
          paddingRight: page === "accueil" || page === "profile" ? 0 : 24,
          paddingBottom: page === "accueil" || page === "profile" ? 0 : 24,
          maxWidth: page === "accueil" || page === "profile" ? "100%" : 1700,
          width: "100%",
          margin: "0 auto",
          alignSelf: "stretch",
          boxSizing: "border-box",
        }}
      >
        {page === "accueil" && <AccueilPage onNavigate={setPage} />}
        {page === "dashboard" && <DashboardPage />}
        {page === "mdx" && <QueryBuilder />}
        {page === "attribution_role" && <AttributionRoles />} {/* ← AJOUTER */}
        {page === "magazin" && <MagasinView />}
        {page === "marques" && <MarqueView />}
        {page === "ventes" && <VentesView />}
        {page === "sousfamille" && <SousFamilleView />}
        {page === "saison" && <SaisonView />}
        {page === "groupe" && <GroupeView />}
        {page === "univers" && <UniversView />}
        {page === "taille" && <TailleView />}
        {page === "couleur" && <CouleurView />}
        {page === "couleur-taille" && <CouleurTailleView />}{" "}
        {/* ← temporaire */}
        {page === "famille" && <FamilleView />}
        {page === "vendeur" && <VendeurView />}
        {page === "prevision_f" && <PrevisionFContainer />}
        {page === "profile" && ( // ← AJOUTER
          <ProfilePage onLogout={() => window.location.reload()} />
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Home;
